import type {BufferAttribute, InterleavedBufferAttribute} from 'three/webgpu';

/**
 * 将 THREE 原生累计线距离转换为与顶点一一对应的距离百分比。
 *
 * 设计说明：
 * 1. 输入必须来自 `Line.computeLineDistances()` 生成的 `lineDistance` attribute；
 * 2. 空 attribute 返回空数组，避免读取不存在的末端距离；
 * 3. 总距离非正时返回全零数组，避免除零产生 `NaN` 或无穷值。
 *
 * @param lineDistanceAttribute THREE 原生累计线距离 attribute。
 * @returns 与顶点一一对应的距离百分比数组。
 */
export function createLineDistancePercentages(
    lineDistanceAttribute: BufferAttribute | InterleavedBufferAttribute,
): number[] {
    const vertexCount = lineDistanceAttribute.count;
    if (vertexCount === 0) {
        return [];
    }

    const totalDistance = lineDistanceAttribute.getX(vertexCount - 1);
    const lineDistancePercentages = new Array<number>(vertexCount).fill(0);

    // 退化线段没有可归一化的总长度，保留全零结果供材质稳定读取。
    if (totalDistance <= 0) {
        return lineDistancePercentages;
    }

    // THREE 已提供累计距离，这里只做一次线性归一化，不重复计算几何长度。
    for (let index = 0; index < vertexCount; index += 1) {
        lineDistancePercentages[index] = lineDistanceAttribute.getX(index) / totalDistance;
    }

    return lineDistancePercentages;
}
